import React from 'react';

import EntryActions from 'actions/EntryActions';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {areAllAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import CreateEntryFromTemplateButtonBase, {
  CreateEntryFromTemplateSubmission,
} from 'notebook/entry-templates/CreateEntryFromTemplateButtonBase';
import LegacyCreateEntryFromTemplateModalClass from 'notebook/entry-templates/LegacyCreateEntryFromTemplateModal';
import {
  TemplatePublishingRecordsQuery,
  TemplatePublishingRecordsQueryVariables,
} from 'template-collectibles/publishing-ui/graphql/__generated/TemplatePublishingRecordsQuery';
import {TEMPLATE_PUBLISHING_RECORDS_QUERY} from 'template-collectibles/publishing-ui/graphql/TemplatePublishingRecordsQuery';

export const CREATE_ENTRY_FROM_DRAFT_BUTTON_TEXT = 'Create entry from draft';

export const ARCHIVED_DRAFT_TEMPLATE_MESSAGE =
  'To create an entry from a draft, please unarchive this template';

export const LATEST_DRAFT_VERSION_REQUIRED_MESSAGE =
  'To create an entry from a draft, please select the latest version of this template';

export const NO_DRAFT_VERSION_MESSAGE = 'The latest version is published, so this template has no draft';

export const DRAFT_STATUS_UNAVAILABLE_MESSAGE =
  'Draft status is unavailable. Please wait for the template to finish loading';

function getDraftDisabledMessage(
  result: SkippableBenchlingQueryResult<
    TemplatePublishingRecordsQuery,
    TemplatePublishingRecordsQueryVariables
  >,
  openVersionApiId: string | undefined,
  isViewingLatestVersion: boolean
): string | null {
  if (!isViewingLatestVersion) {
    return LATEST_DRAFT_VERSION_REQUIRED_MESSAGE;
  }
  if (
    openVersionApiId == null ||
    result.type !== QueryResultType.SUCCESS ||
    isInaccessible(result.data.template) ||
    !areAllAccessible(result.data.template.publishingRecords)
  ) {
    return DRAFT_STATUS_UNAVAILABLE_MESSAGE;
  }
  return result.data.template.publishingRecords.some(
    (record) => record.publishableVersionId === openVersionApiId
  )
    ? NO_DRAFT_VERSION_MESSAGE
    : null;
}

function handleCreateEntryFromDraftTemplate({
  entryName,
  folderId,
  templateId,
  tagSchemaId,
  schemaTags,
  initialDay,
  studyAssociations,
}: CreateEntryFromTemplateSubmission): void {
  EntryActions.createFromDraftTemplate({
    entryName,
    folderId,
    templateId,
    tagSchemaId,
    schemaTags,
    initialDay,
    studyAssociations,
    /* eslint-disable-next-line @typescript-eslint/unbound-method */
    onSuccess: LegacyCreateEntryFromTemplateModalClass.close,
  });
}

interface CreateEntryFromDraftTemplateButtonProps {
  templateId: string;
  isTemplateArchived: boolean;
  openVersionApiId: string | undefined;
  isViewingLatestVersion: boolean;
}

export default function CreateEntryFromDraftTemplateButton({
  templateId,
  isTemplateArchived,
  openVersionApiId,
  isViewingLatestVersion,
}: CreateEntryFromDraftTemplateButtonProps): JSX.Element {
  const publishingRecordsResult = useSkippableBenchlingQuery<
    TemplatePublishingRecordsQuery,
    TemplatePublishingRecordsQueryVariables
  >(TEMPLATE_PUBLISHING_RECORDS_QUERY, {variables: {templateId}});

  const disabledMessage = isTemplateArchived
    ? ARCHIVED_DRAFT_TEMPLATE_MESSAGE
    : getDraftDisabledMessage(publishingRecordsResult, openVersionApiId, isViewingLatestVersion);

  return (
    <CreateEntryFromTemplateButtonBase
      buttonId="create-entry-from-draft-button"
      buttonText={CREATE_ENTRY_FROM_DRAFT_BUTTON_TEXT}
      disabledMessage={disabledMessage}
      filterTemplateIds={[templateId]}
      onRequestSubmit={handleCreateEntryFromDraftTemplate}
      showUnpublishedCollectibles={true}
      templateId={templateId}
    />
  );
}
